import { api } from "@/lib/api-client";
import type { Paginated } from "../agents/api";

export type TemplateDirection = "inbound" | "outbound";

export type TemplateVariable = {
  name: string;
  label: string;
  type: string;
  required: boolean;
  scope: "system" | "user";
  group: string;
  default: string;
};

export type TemplateFaq = { q: string; a: string };

export type TemplateFlowStep = { step: string; text: string };

export type TemplateFlow = {
  steps?: TemplateFlowStep[];
  nodes?: unknown[];
  edges?: unknown[];
};

export type LibraryTemplate = {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  direction: TemplateDirection;
  direction_display?: string;
  system_prompt: string;
  greeting: string;
  faq: TemplateFaq[];
  variables: TemplateVariable[];
  flow: TemplateFlow;
  tools: string[];
  rules: string;
  source: "import" | "manual" | "ai";
  status: "draft" | "published";
  is_active: boolean;
  sort_order: number;
  tenant: string | null;
  is_platform: boolean;
  created_at?: string;
  updated_at: string;
};

/** Trimmed shape returned by the list endpoint (the "basket" grid). */
export type LibraryTemplateSummary = Pick<
  LibraryTemplate,
  | "id"
  | "name"
  | "description"
  | "category"
  | "icon"
  | "direction"
  | "source"
  | "status"
  | "is_active"
  | "sort_order"
  | "tenant"
  | "is_platform"
  | "created_at"
  | "updated_at"
> & { variable_count: number };

/** Draft returned by import/generate before it is saved. */
export type TemplateDraft = Omit<
  LibraryTemplate,
  | "id"
  | "tenant"
  | "is_platform"
  | "status"
  | "is_active"
  | "sort_order"
  | "updated_at"
  | "created_at"
  | "direction_display"
  | "icon"
>;

export type TemplateWritePayload = Partial<
  Pick<
    LibraryTemplate,
    | "name"
    | "description"
    | "category"
    | "icon"
    | "direction"
    | "system_prompt"
    | "greeting"
    | "faq"
    | "flow"
    | "tools"
    | "rules"
    | "source"
    | "status"
    | "is_active"
    | "sort_order"
  >
>;

const BASE = "/template-library/";

export const templatesApi = {
  list: async () => {
    const res = await api.get<Paginated<LibraryTemplateSummary> | LibraryTemplateSummary[]>(
      BASE,
    );
    return Array.isArray(res.data) ? res.data : res.data.results;
  },

  get: async (id: string) => {
    const res = await api.get<LibraryTemplate>(`${BASE}${id}/`);
    return res.data;
  },

  create: async (data: TemplateWritePayload) => {
    const res = await api.post<LibraryTemplate>(BASE, data);
    return res.data;
  },

  update: async (id: string, data: TemplateWritePayload) => {
    const res = await api.patch<LibraryTemplate>(`${BASE}${id}/`, data);
    return res.data;
  },

  remove: async (id: string) => {
    await api.delete(`${BASE}${id}/`);
  },

  /** Parse an uploaded .docx/.md/.txt into a draft (nothing saved). */
  importFile: async (file: File) => {
    const form = new FormData();
    form.append("file", file);
    const res = await api.post<TemplateDraft>(`${BASE}import/`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  },

  /** Draft a template from a short brief via the tenant's OpenAI key. */
  generate: async (brief: string, direction: TemplateDirection) => {
    const res = await api.post<TemplateDraft>(`${BASE}generate/`, {
      brief,
      direction,
    });
    return res.data;
  },
};
